import axios from 'axios';

// Use REACT_APP_BACKEND_URL if available, otherwise fallback to relative /api
const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

// Helper: cek apakah JWT token sudah expired (decode payload tanpa verifikasi signature)
const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (!payload.exp) return false; // no exp = never expires
    return payload.exp * 1000 < Date.now();
  } catch {
    return true; // malformed = treat as expired
  }
};

// Endpoints yang benar-benar auth-critical (memerlukan force logout kalau 401)
const AUTH_CRITICAL_PATTERNS = [
  '/auth/me',
  '/auth/verify',
  '/auth/refresh',
];

const isAuthCritical = (url = '') => AUTH_CRITICAL_PATTERNS.some(p => url.includes(p));

// Setup axios interceptor for 401 responses — SMART logout
// Hanya force-logout kalau: (a) token benar-benar expired, ATAU (b) request ke auth-critical endpoint
// Ini mencegah user dikeluarkan paksa hanya karena 1 endpoint kebetulan return 401.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      const token = localStorage.getItem('token');
      const url = error.config?.url || '';
      const shouldLogout = isTokenExpired(token) || isAuthCritical(url);
      if (shouldLogout) {
        console.warn('[auth] Force logout — token expired or auth-critical endpoint failed:', url);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      } else {
        console.warn('[auth] 401 di endpoint non-critical, TIDAK force logout:', url);
      }
    }
    return Promise.reject(error);
  }
);

// Add timestamp to prevent caching
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  const entityId = localStorage.getItem('activeEntityId');
  const headers = { 
    Authorization: `Bearer ${token}`,
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache'
  };
  if (entityId) headers['X-Entity-Id'] = entityId;
  return { 
    headers,
    params: {
      _t: Date.now() // Cache buster
    }
  };
};

// Helper to download file with auth
const downloadWithAuth = async (url, filename) => {
  const token = localStorage.getItem('token');
  const entityId = localStorage.getItem('activeEntityId');
  const headers = { Authorization: `Bearer ${token}` };
  if (entityId) headers['X-Entity-Id'] = entityId;
  try {
    const response = await axios.get(url, {
      headers,
      responseType: 'blob'
    });
    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('Download failed:', error);
    throw error;
  }
};

export const dashboardApi = {
  getMetrics: async () => {
    const response = await axios.get(`${API}/dashboard`, getAuthHeader());
    return response.data;
  },

  // Trend & komparasi (% vs bulan lalu) untuk KPI utama
  getDashboardTrends: async () => {
    const response = await axios.get(`${API}/dashboard/trends`, getAuthHeader());
    return response.data;
  },

  // Top 5 Performers — supplier/buyer/expense bulan ini
  getTopPerformers: async (limit = 5) => {
    const response = await axios.get(`${API}/dashboard/top-performers?limit=${limit}`, getAuthHeader());
    return response.data;
  },

  // AI Insight Cards (narrative dari LLM, cached 6 jam)
  getAIInsights: async (refresh = false) => {
    const response = await axios.get(`${API}/dashboard/ai-insights?refresh=${refresh}`, getAuthHeader());
    return response.data;
  },
  
  // Cashflow - Auto Generated (READ ONLY)
  getCashflowSummary: async () => {
    const response = await axios.get(`${API}/cashflow/summary`, getAuthHeader());
    return response.data;
  },
  
  getCashflowTransactions: async (params = {}) => {
    // Optional server-side filter for performance (CV KANSHA: 3500+ records)
    const qs = new URLSearchParams();
    if (params.start_date) qs.append('start_date', params.start_date);
    if (params.end_date) qs.append('end_date', params.end_date);
    if (params.limit) qs.append('limit', params.limit);
    const url = qs.toString() ? `${API}/cashflow/transactions?${qs}` : `${API}/cashflow/transactions`;
    const response = await axios.get(url, getAuthHeader());
    return response.data;
  },
  
  getCashflow: async () => {
    const response = await axios.get(`${API}/cashflow`, getAuthHeader());
    return response.data;
  },
  
  // Transaction Analytics
  getTransactionAnalytics: async () => {
    const response = await axios.get(`${API}/transactions/analytics`, getAuthHeader());
    return response.data;
  },
  
  // Tonnage Summary per Location
  getTonnageSummary: async (startDate = null, endDate = null) => {
    const config = getAuthHeader();
    // Add date filter params to existing params
    if (startDate) config.params.start_date = startDate;
    if (endDate) config.params.end_date = endDate;
    
    const response = await axios.get(`${API}/transactions/tonnage-summary`, config);
    return response.data;
  },
  
  getPurchases: async (params = {}) => {
    // Optional server-side filter: params = { start_date, end_date, limit }
    const qs = new URLSearchParams();
    if (params.start_date) qs.append('start_date', params.start_date);
    if (params.end_date) qs.append('end_date', params.end_date);
    if (params.limit) qs.append('limit', params.limit);
    const url = qs.toString() ? `${API}/transactions/purchases?${qs}` : `${API}/transactions/purchases`;
    const response = await axios.get(url, getAuthHeader());
    return response.data;
  },
  
  createPurchase: async (data) => {
    const response = await axios.post(`${API}/transactions/purchases`, data, getAuthHeader());
    return response.data;
  },
  
  createMultiPurchase: async (data, entityId) => {
    const config = getAuthHeader();
    if (entityId) config.headers['X-Entity-Id'] = entityId;
    config.timeout = 30000; // 30s timeout — supaya hang tidak jadi infinite spinner
    const response = await axios.post(`${API}/transactions/purchases/multi`, data, config);
    return response.data;
  },
  
  updatePurchase: async (id, data) => {
    const response = await axios.put(`${API}/transactions/purchases/${id}`, data, getAuthHeader());
    return response.data;
  },
  
  deletePurchase: async (id) => {
    const response = await axios.delete(`${API}/transactions/purchases/${id}`, getAuthHeader());
    return response.data;
  },
  
  getSales: async (params = {}) => {
    const qs = new URLSearchParams();
    if (params.start_date) qs.append('start_date', params.start_date);
    if (params.end_date) qs.append('end_date', params.end_date);
    if (params.limit) qs.append('limit', params.limit);
    const url = qs.toString() ? `${API}/transactions/sales?${qs}` : `${API}/transactions/sales`;
    const response = await axios.get(url, getAuthHeader());
    return response.data;
  },
  
  createSale: async (data) => {
    const response = await axios.post(`${API}/transactions/sales`, data, getAuthHeader());
    return response.data;
  },
  
  createMultiSale: async (data, entityId) => {
    const config = getAuthHeader();
    if (entityId) config.headers['X-Entity-Id'] = entityId;
    config.timeout = 30000; // 30s timeout — supaya hang tidak jadi infinite spinner
    const response = await axios.post(`${API}/transactions/sales/multi`, data, config);
    return response.data;
  },
  
  updateSale: async (id, data) => {
    const response = await axios.put(`${API}/transactions/sales/${id}`, data, getAuthHeader());
    return response.data;
  },
  
  deleteSale: async (id) => {
    const response = await axios.delete(`${API}/transactions/sales/${id}`, getAuthHeader());
    return response.data;
  },
  
  getDebts: async () => {
    const response = await axios.get(`${API}/debts`, getAuthHeader());
    return response.data;
  },
  
  getDebtsAnalytics: async () => {
    const response = await axios.get(`${API}/debts/analytics`, getAuthHeader());
    return response.data;
  },
  
  createDebt: async (data) => {
    const response = await axios.post(`${API}/debts`, data, getAuthHeader());
    return response.data;
  },
  
  updateDebt: async (id, data) => {
    const response = await axios.put(`${API}/debts/${id}`, data, getAuthHeader());
    return response.data;
  },
  
  deleteDebt: async (id) => {
    const response = await axios.delete(`${API}/debts/${id}`, getAuthHeader());
    return response.data;
  },
  
  createDebtPayment: async (data) => {
    const response = await axios.post(`${API}/debts/payments`, data, getAuthHeader());
    return response.data;
  },

  getDebtPaymentHistory: async (debtId = null) => {
    const params = debtId ? `?debt_id=${debtId}` : '';
    const response = await axios.get(`${API}/debts/payments/history${params}`, getAuthHeader());
    return response.data;
  },

  exportDebtPaymentHistoryExcel: async (debtId = null) => {
    const params = debtId ? `?debt_id=${debtId}` : '';
    const filename = `laporan_pembayaran_hutang_${new Date().toISOString().slice(0,10)}.xlsx`;
    await downloadWithAuth(`${API}/debts/payments/history/export/excel${params}`, filename);
  },

  exportDebtPaymentHistoryPdf: async (debtId = null) => {
    const params = debtId ? `?debt_id=${debtId}` : '';
    const filename = `laporan_pembayaran_hutang_${new Date().toISOString().slice(0,10)}.pdf`;
    await downloadWithAuth(`${API}/debts/payments/history/export/pdf${params}`, filename);
  },
  
  getReceivables: async () => {
    const response = await axios.get(`${API}/receivables`, getAuthHeader());
    return response.data;
  },
  
  getReceivablesAnalytics: async () => {
    const response = await axios.get(`${API}/receivables/analytics`, getAuthHeader());
    return response.data;
  },
  
  createReceivable: async (data) => {
    const response = await axios.post(`${API}/receivables`, data, getAuthHeader());
    return response.data;
  },
  
  updateReceivable: async (id, data) => {
    const response = await axios.put(`${API}/receivables/${id}`, data, getAuthHeader());
    return response.data;
  },
  
  deleteReceivable: async (id) => {
    const response = await axios.delete(`${API}/receivables/${id}`, getAuthHeader());
    return response.data;
  },
  
  createCollection: async (data) => {
    const response = await axios.post(`${API}/receivables/collections`, data, getAuthHeader());
    return response.data;
  },

  getReceivableCollectionHistory: async (receivableId = null) => {
    const params = receivableId ? `?receivable_id=${receivableId}` : '';
    const response = await axios.get(`${API}/receivables/collections/history${params}`, getAuthHeader());
    return response.data;
  },

  exportReceivableCollectionHistoryExcel: async (receivableId = null) => {
    const params = receivableId ? `?receivable_id=${receivableId}` : '';
    const filename = `rekap_pelunasan_piutang_${new Date().toISOString().slice(0,10)}.xlsx`;
    await downloadWithAuth(`${API}/receivables/collections/history/export/excel${params}`, filename);
  },

  exportReceivableCollectionHistoryPdf: async (receivableId = null) => {
    const params = receivableId ? `?receivable_id=${receivableId}` : '';
    const filename = `rekap_pelunasan_piutang_${new Date().toISOString().slice(0,10)}.pdf`;
    await downloadWithAuth(`${API}/receivables/collections/history/export/pdf${params}`, filename);
  },
  
  getInvestors: async () => {
    const response = await axios.get(`${API}/investors`, getAuthHeader());
    return response.data;
  },

  getCapitalHistory: async (investorId = null) => {
    const params = investorId ? `?investor_id=${investorId}` : '';
    const response = await axios.get(`${API}/investors/capital-history${params}`, getAuthHeader());
    return response.data;
  },

  exportCapitalHistoryExcel: async (investorId = null) => {
    const params = investorId ? `?investor_id=${investorId}` : '';
    const filename = `histori_modal_investor_${new Date().toISOString().slice(0,10)}.xlsx`;
    await downloadWithAuth(`${API}/investors/capital-history/export/excel${params}`, filename);
  },

  exportCapitalHistoryPdf: async (investorId = null) => {
    const params = investorId ? `?investor_id=${investorId}` : '';
    const filename = `histori_modal_investor_${new Date().toISOString().slice(0,10)}.pdf`;
    await downloadWithAuth(`${API}/investors/capital-history/export/pdf${params}`, filename);
  },

  deleteCapitalHistory: async (historyId, restoreCapital = false) => {
    const response = await axios.delete(`${API}/investors/capital-history/${historyId}?restore_capital=${restoreCapital}`, getAuthHeader());
    return response.data;
  },

  recomputeCapitalHistory: async (investorId, startingCapital = null) => {
    const params = startingCapital !== null && startingCapital !== '' && !isNaN(startingCapital)
      ? `?starting_capital=${Number(startingCapital)}`
      : '';
    const response = await axios.post(`${API}/investors/${investorId}/recompute-capital-history${params}`, {}, getAuthHeader());
    return response.data;
  },

  getCapitalHistoryDebug: async (investorId) => {
    const response = await axios.get(`${API}/investors/${investorId}/capital-history-debug`, getAuthHeader());
    return response.data;
  },

  auditAllInvestors: async () => {
    const response = await axios.get(`${API}/investors/audit-all`, getAuthHeader());
    return response.data;
  },

  bulkDistributeProfit: async (payload) => {
    const response = await axios.post(`${API}/investors/distributions/bulk-distribute`, payload, getAuthHeader());
    return response.data;
  },

  exportDistributionsExcel: async (month, year) => {
    const params = (month && year) ? `?month=${month}&year=${year}` : '';
    const response = await axios.get(`${API}/investors/distributions/export/excel${params}`, {
      ...getAuthHeader(),
      responseType: 'blob',
    });
    return response.data;
  },

  // Entity Migration
  previewMigration: async (sourceEntityId, nameFilter = '', buyerFilter = '', supplierFilter = '', supplierNameFilter = '') => {
    const params = new URLSearchParams({ source_entity_id: sourceEntityId });
    if (nameFilter) params.append('name_filter', nameFilter);
    if (buyerFilter) params.append('buyer_filter', buyerFilter);
    if (supplierFilter) params.append('supplier_filter', supplierFilter);
    if (supplierNameFilter) params.append('supplier_name_filter', supplierNameFilter);
    const response = await axios.get(`${API}/entities/migration-preview?${params}`, getAuthHeader());
    return response.data;
  },

  migrateEntityData: async (data) => {
    const response = await axios.post(`${API}/entities/migrate-data`, data, getAuthHeader());
    return response.data;
  },

  getDistinctBuyers: async (entityId = '') => {
    const params = entityId ? `?entity_id=${entityId}` : '';
    const response = await axios.get(`${API}/entities/distinct-buyers${params}`, getAuthHeader());
    return response.data;
  },

  getDistinctSuppliers: async (entityId = '') => {
    const params = entityId ? `?entity_id=${entityId}` : '';
    const response = await axios.get(`${API}/entities/distinct-suppliers${params}`, getAuthHeader());
    return response.data;
  },

  getDistinctSupplierNames: async (entityId = '') => {
    const params = entityId ? `?entity_id=${entityId}` : '';
    const response = await axios.get(`${API}/entities/distinct-supplier-names${params}`, getAuthHeader());
    return response.data;
  },

  getMigrationPreviewDetail: async (sourceEntityId, collection, filters = {}) => {
    const params = new URLSearchParams({ source_entity_id: sourceEntityId, collection });
    if (filters.nameFilter) params.append('name_filter', filters.nameFilter);
    if (filters.buyerFilter) params.append('buyer_filter', filters.buyerFilter);
    if (filters.supplierFilter) params.append('supplier_filter', filters.supplierFilter);
    if (filters.supplierNameFilter) params.append('supplier_name_filter', filters.supplierNameFilter);
    if (filters.page) params.append('page', filters.page);
    const response = await axios.get(`${API}/entities/migration-preview-detail?${params}`, getAuthHeader());
    return response.data;
  },

  assignUserEntities: async (userId, entityIds) => {
    const response = await axios.post(`${API}/entities/assign-user`, { user_id: userId, entity_ids: entityIds }, getAuthHeader());
    return response.data;
  },
  
  createInvestor: async (data) => {
    const response = await axios.post(`${API}/investors`, data, getAuthHeader());
    return response.data;
  },
  
  updateInvestor: async (id, data) => {
    const response = await axios.put(`${API}/investors/${id}`, data, getAuthHeader());
    return response.data;
  },
  
  deleteInvestor: async (id) => {
    const response = await axios.delete(`${API}/investors/${id}`, getAuthHeader());
    return response.data;
  },

  updateInvestorCapital: async (id, data) => {
    const params = new URLSearchParams();
    params.append('action', data.action);
    params.append('amount', data.amount);
    if (data.notes) params.append('notes', data.notes);
    if (data.transaction_date) params.append('transaction_date', data.transaction_date);
    const response = await axios.post(`${API}/investors/${id}/capital?${params.toString()}`, {}, getAuthHeader());
    return response.data;
  },

  createDistribution: async (data) => {
    const params = new URLSearchParams();
    params.append('investor_id', data.investor_id);
    params.append('amount', data.amount);
    params.append('period', data.period);
    params.append('notes', data.notes || '');
    const response = await axios.post(`${API}/investors/distributions?${params.toString()}`, {}, getAuthHeader());
    return response.data;
  },
  
  getTaxes: async () => {
    const response = await axios.get(`${API}/taxes`, getAuthHeader());
    return response.data;
  },
  
  createTax: async (data) => {
    const response = await axios.post(`${API}/taxes`, data, getAuthHeader());
    return response.data;
  },
  
  updateTax: async (id, data) => {
    const response = await axios.put(`${API}/taxes/${id}`, data, getAuthHeader());
    return response.data;
  },
  
  deleteTax: async (id) => {
    const response = await axios.delete(`${API}/taxes/${id}`, getAuthHeader());
    return response.data;
  },

  getPPH22Report: async (startDate = null, endDate = null) => {
    let params = '';
    if (startDate || endDate) {
      const queryParams = [];
      if (startDate) queryParams.push(`start_date=${startDate}`);
      if (endDate) queryParams.push(`end_date=${endDate}`);
      params = '?' + queryParams.join('&');
    }
    const response = await axios.get(`${API}/taxes/pph22-report${params}`, getAuthHeader());
    return response.data;
  },

  exportPPH22ReportExcel: async (startDate = null, endDate = null) => {
    let params = '';
    if (startDate || endDate) {
      const queryParams = [];
      if (startDate) queryParams.push(`start_date=${startDate}`);
      if (endDate) queryParams.push(`end_date=${endDate}`);
      params = '?' + queryParams.join('&');
    }
    const filename = `laporan_pph22_${new Date().toISOString().slice(0,10)}.xlsx`;
    await downloadWithAuth(`${API}/taxes/pph22-report/export/excel${params}`, filename);
  },

  exportPPH22ReportPdf: async (startDate = null, endDate = null) => {
    let params = '';
    if (startDate || endDate) {
      const queryParams = [];
      if (startDate) queryParams.push(`start_date=${startDate}`);
      if (endDate) queryParams.push(`end_date=${endDate}`);
      params = '?' + queryParams.join('&');
    }
    const filename = `laporan_pph22_${new Date().toISOString().slice(0,10)}.pdf`;
    await downloadWithAuth(`${API}/taxes/pph22-report/export/pdf${params}`, filename);
  },

  // Other Income APIs
  getOtherIncome: async () => {
    const response = await axios.get(`${API}/other-income`, getAuthHeader());
    return response.data;
  },

  getOtherIncomeAnalytics: async () => {
    const response = await axios.get(`${API}/other-income/analytics`, getAuthHeader());
    return response.data;
  },

  createOtherIncome: async (data) => {
    const response = await axios.post(`${API}/other-income`, data, getAuthHeader());
    return response.data;
  },

  updateOtherIncome: async (id, data) => {
    const response = await axios.put(`${API}/other-income/${id}`, data, getAuthHeader());
    return response.data;
  },

  deleteOtherIncome: async (id) => {
    const response = await axios.delete(`${API}/other-income/${id}`, getAuthHeader());
    return response.data;
  },

  exportOtherIncomeExcel: async (startDate = null, endDate = null) => {
    let params = '';
    if (startDate || endDate) {
      const queryParams = [];
      if (startDate) queryParams.push(`start_date=${startDate}`);
      if (endDate) queryParams.push(`end_date=${endDate}`);
      params = '?' + queryParams.join('&');
    }
    const filename = `pendapatan_lain_${new Date().toISOString().slice(0,10)}.xlsx`;
    await downloadWithAuth(`${API}/other-income/export/excel${params}`, filename);
  },
  
  getPayroll: async () => {
    const response = await axios.get(`${API}/hr/payroll`, getAuthHeader());
    return response.data;
  },
  
  createPayroll: async (data) => {
    const response = await axios.post(`${API}/hr/payroll`, data, getAuthHeader());
    return response.data;
  },
  
  updatePayroll: async (id, data) => {
    const response = await axios.put(`${API}/hr/payroll/${id}`, data, getAuthHeader());
    return response.data;
  },
  
  deletePayroll: async (id) => {
    const response = await axios.delete(`${API}/hr/payroll/${id}`, getAuthHeader());
    return response.data;
  },
  
  markPayrollPaid: async (id) => {
    const response = await axios.put(`${API}/hr/payroll/${id}/mark-paid`, {}, getAuthHeader());
    return response.data;
  },

  previewOvertimeForPayroll: async (employeeId, employeeName, period) => {
    const params = new URLSearchParams();
    if (employeeId) params.set('employee_id', employeeId);
    if (employeeName) params.set('employee_name', employeeName);
    if (period) params.set('period', period);
    const response = await axios.get(`${API}/hr/payroll/preview-overtime?${params.toString()}`, getAuthHeader());
    return response.data;
  },

  repairPayrollOvertime: async (payload) => {
    const response = await axios.post(`${API}/system/admin/repair-payroll-overtime`, payload, getAuthHeader());
    return response.data;
  },
  
  getExpenses: async () => {
    const response = await axios.get(`${API}/hr/expenses`, getAuthHeader());
    return response.data;
  },
  
  createExpense: async (data) => {
    const response = await axios.post(`${API}/hr/expenses`, data, getAuthHeader());
    return response.data;
  },
  
  updateExpense: async (id, data) => {
    const response = await axios.put(`${API}/hr/expenses/${id}`, data, getAuthHeader());
    return response.data;
  },
  
  deleteExpense: async (id) => {
    const response = await axios.delete(`${API}/hr/expenses/${id}`, getAuthHeader());
    return response.data;
  },
  
  // Employee Management
  getEmployees: async () => {
    const response = await axios.get(`${API}/hr/employees`, getAuthHeader());
    return response.data;
  },
  
  createEmployee: async (data) => {
    const response = await axios.post(`${API}/hr/employees`, data, getAuthHeader());
    return response.data;
  },
  
  updateEmployee: async (id, data) => {
    const response = await axios.put(`${API}/hr/employees/${id}`, data, getAuthHeader());
    return response.data;
  },
  
  deleteEmployee: async (id) => {
    const response = await axios.delete(`${API}/hr/employees/${id}`, getAuthHeader());
    return response.data;
  },
  
  // Attendance
  getAttendance: async () => {
    const response = await axios.get(`${API}/hr/attendance`, getAuthHeader());
    return response.data;
  },
  
  createAttendance: async (data) => {
    const response = await axios.post(`${API}/hr/attendance`, data, getAuthHeader());
    return response.data;
  },
  
  updateAttendance: async (id, data) => {
    const response = await axios.put(`${API}/hr/attendance/${id}`, data, getAuthHeader());
    return response.data;
  },
  
  deleteAttendance: async (id) => {
    const response = await axios.delete(`${API}/hr/attendance/${id}`, getAuthHeader());
    return response.data;
  },
  
  // Leave
  getLeaves: async () => {
    const response = await axios.get(`${API}/hr/leaves`, getAuthHeader());
    return response.data;
  },
  
  createLeave: async (data) => {
    const response = await axios.post(`${API}/hr/leaves`, data, getAuthHeader());
    return response.data;
  },

  // Get download URL untuk attachment cuti (pakai token via header)
  getLeaveAttachmentUrl: (leaveId) => `${API}/hr/leaves/${leaveId}/attachment`,
  
  updateLeave: async (id, data) => {
    const response = await axios.put(`${API}/hr/leaves/${id}`, data, getAuthHeader());
    return response.data;
  },
  
  deleteLeave: async (id) => {
    const response = await axios.delete(`${API}/hr/leaves/${id}`, getAuthHeader());
    return response.data;
  },
  
  updateLeaveStatus: async (id, status) => {
    const response = await axios.patch(`${API}/hr/leaves/${id}/status?status=${status}`, {}, getAuthHeader());
    return response.data;
  },

  approveLeave: async (id) => {
    const response = await axios.put(`${API}/hr/leaves/${id}/approve`, {}, getAuthHeader());
    return response.data;
  },

  rejectLeave: async (id, rejectionReason) => {
    const response = await axios.put(`${API}/hr/leaves/${id}/reject`, { rejection_reason: rejectionReason }, getAuthHeader());
    return response.data;
  },

  // Employee Portal APIs
  getMyProfile: async () => {
    const response = await axios.get(`${API}/hr/my-profile`, getAuthHeader());
    return response.data;
  },

  submitMyLeaveRequest: async (data) => {
    const response = await axios.post(`${API}/hr/my-leave-request`, data, getAuthHeader());
    return response.data;
  },

  getMyLeaves: async () => {
    const response = await axios.get(`${API}/hr/my-leaves`, getAuthHeader());
    return response.data;
  },

  getMyAttendance: async () => {
    const response = await axios.get(`${API}/hr/my-attendance`, getAuthHeader());
    return response.data;
  },

  getMyNotifications: async () => {
    const response = await axios.get(`${API}/hr/my-notifications`, getAuthHeader());
    return response.data;
  },

  markNotificationRead: async (id) => {
    const response = await axios.put(`${API}/hr/my-notifications/${id}/read`, {}, getAuthHeader());
    return response.data;
  },

  markAllNotificationsRead: async () => {
    const response = await axios.put(`${API}/hr/my-notifications/read-all`, {}, getAuthHeader());
    return response.data;
  },

  // Profile Link Request (self-service for karyawan)
  getUnlinkedEmployees: async () => {
    const response = await axios.get(`${API}/hr/unlinked-employees`, getAuthHeader());
    return response.data;
  },
  requestProfileLink: async (data) => {
    const response = await axios.post(`${API}/hr/request-profile-link`, data, getAuthHeader());
    return response.data;
  },
  getMyProfileLinkRequest: async () => {
    const response = await axios.get(`${API}/hr/my-profile-link-request`, getAuthHeader());
    return response.data;
  },
  listProfileLinkRequests: async (status = null) => {
    const url = status ? `${API}/hr/profile-link-requests?status=${status}` : `${API}/hr/profile-link-requests`;
    const response = await axios.get(url, getAuthHeader());
    return response.data;
  },
  approveProfileLinkRequest: async (requestId, payload = {}) => {
    const response = await axios.post(`${API}/hr/profile-link-requests/${requestId}/approve`, payload, getAuthHeader());
    return response.data;
  },
  rejectProfileLinkRequest: async (requestId, reason) => {
    const response = await axios.post(`${API}/hr/profile-link-requests/${requestId}/reject`, { reason }, getAuthHeader());
    return response.data;
  },

  // Leave Balance
  getEmployeeLeaveBalance: async (employeeId) => {
    const response = await axios.get(`${API}/hr/employees/${employeeId}/leave-balance`, getAuthHeader());
    return response.data;
  },

  // Resync leave balances (one-shot fix untuk data historical)
  resyncLeaveBalances: async () => {
    const response = await axios.post(`${API}/hr/leaves/resync-balances`, {}, getAuthHeader());
    return response.data;
  },

  // Holiday Calendar (Indonesian national holidays)
  getHolidays: async (year = null) => {
    const url = year ? `${API}/hr/holidays?year=${year}` : `${API}/hr/holidays`;
    const response = await axios.get(url, getAuthHeader());
    return response.data;
  },

  syncHolidaysFromAPI: async (year = null) => {
    const url = year ? `${API}/hr/holidays/sync?year=${year}` : `${API}/hr/holidays/sync`;
    const response = await axios.post(url, {}, getAuthHeader());
    return response.data;
  },

  createCustomHoliday: async (data) => {
    const response = await axios.post(`${API}/hr/holidays`, data, getAuthHeader());
    return response.data;
  },

  updateHoliday: async (id, data) => {
    const response = await axios.patch(`${API}/hr/holidays/${id}`, data, getAuthHeader());
    return response.data;
  },

  deleteHoliday: async (id) => {
    const response = await axios.delete(`${API}/hr/holidays/${id}`, getAuthHeader());
    return response.data;
  },

  checkDateHoliday: async (dateStr) => {
    const response = await axios.get(`${API}/hr/holidays/check/${dateStr}`, getAuthHeader());
    return response.data;
  },

  // Scheduler admin
  getSchedulerStatus: async () => {
    const response = await axios.get(`${API}/admin/scheduler/status`, getAuthHeader());
    return response.data;
  },

  triggerSchedulerJob: async (jobId) => {
    const response = await axios.post(`${API}/admin/scheduler/trigger/${jobId}`, {}, getAuthHeader());
    return response.data;
  },

  getAllLeaveBalances: async () => {
    const response = await axios.get(`${API}/hr/leave-balances`, getAuthHeader());
    return response.data;
  },

  // Employee Performance
  getEmployeeKpi: async (employeeId) => {
    const response = await axios.get(`${API}/hr/employees/${employeeId}/performance`, getAuthHeader());
    return response.data;
  },

  // Owner Alerts / Notification Center
  getOwnerAlerts: async () => {
    const response = await axios.get(`${API}/dashboard/owner-alerts`, getAuthHeader());
    return response.data;
  },

  // Intelligence: match conflicts (alias bentrok + underutilized employees)
  getMatchConflicts: async (days = 14) => {
    const response = await axios.get(`${API}/intelligence/data-quality/match-conflicts?days=${days}`, getAuthHeader());
    return response.data;
  },

  // Auto-generate attendance (mode: days_back ATAU date range)
  // recheckAlpha=true → record alpha auto-generated sebelumnya akan dievaluasi ulang
  // (berguna ketika transaksi diinput SETELAH auto-generate dijalankan)
  autoGenerateAttendance: async (daysBack = 7, skipWeekends = false, recheckAlpha = true) => {
    const response = await axios.post(
      `${API}/hr/attendance/auto-generate?days_back=${daysBack}&skip_weekends=${skipWeekends}&recheck_alpha=${recheckAlpha}`,
      {},
      getAuthHeader()
    );
    return response.data;
  },

  // Auto-generate attendance dengan rentang tanggal custom
  autoGenerateAttendanceRange: async (startDate, endDate, skipWeekends = false, recheckAlpha = true) => {
    const response = await axios.post(
      `${API}/hr/attendance/auto-generate?start_date=${startDate}&end_date=${endDate}&skip_weekends=${skipWeekends}&recheck_alpha=${recheckAlpha}`,
      {},
      getAuthHeader()
    );
    return response.data;
  },

  // Diagnostic dry-run: cek setup absensi tiap karyawan tanpa insert apapun
  diagnoseAttendanceSetup: async () => {
    const response = await axios.get(`${API}/hr/attendance/diagnose`, getAuthHeader());
    return response.data;
  },

  // Clarify attendance (alpha → hadir/izin/sakit/cuti with reason)
  clarifyAttendance: async (attendanceId, status, reason) => {
    const response = await axios.patch(
      `${API}/hr/attendance/${attendanceId}/clarify`,
      { status, reason },
      getAuthHeader()
    );
    return response.data;
  },

  // Employee self-service: my performance summary
  getMyPerformance: async () => {
    const response = await axios.get(`${API}/hr/my-performance`, getAuthHeader());
    return response.data;
  },
  getMyPayroll: async () => {
    const response = await axios.get(`${API}/hr/my-payroll`, getAuthHeader());
    return response.data;
  },

  getPerformanceSummary: async () => {
    const response = await axios.get(`${API}/hr/performance-summary`, getAuthHeader());
    return response.data;
  },

  exportEmployeePerformance: async (employeeId, format = 'excel') => {
    const response = await axios.get(
      `${API}/hr/employees/${employeeId}/performance/export?format=${format}`,
      { ...getAuthHeader(), responseType: 'blob' }
    );
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const ext = format === 'pdf' ? 'pdf' : 'xlsx';
    link.setAttribute('download', `kinerja_karyawan_${Date.now()}.${ext}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return true;
  },
  
  // Overtime
  getOvertime: async () => {
    const response = await axios.get(`${API}/hr/overtime`, getAuthHeader());
    return response.data;
  },
  
  createOvertime: async (data) => {
    const response = await axios.post(`${API}/hr/overtime`, data, getAuthHeader());
    return response.data;
  },
  
  updateOvertime: async (id, data) => {
    const response = await axios.put(`${API}/hr/overtime/${id}`, data, getAuthHeader());
    return response.data;
  },
  
  deleteOvertime: async (id) => {
    const response = await axios.delete(`${API}/hr/overtime/${id}`, getAuthHeader());
    return response.data;
  },
  
  updateOvertimeStatus: async (id, status) => {
    const response = await axios.patch(`${API}/hr/overtime/${id}/status?status=${status}`, {}, getAuthHeader());
    return response.data;
  },
  
  // Investor Analytics
  getInvestorAnalytics: async (month = null, year = null) => {
    const params = { ...getAuthHeader().params };
    if (month) params.month = month;
    if (year) params.year = year;
    const response = await axios.get(`${API}/investors/analytics`, {
      ...getAuthHeader(),
      params
    });
    return response.data;
  },
  
  // Tax Analytics
  getTaxAnalytics: async () => {
    const response = await axios.get(`${API}/taxes/analytics`, getAuthHeader());
    return response.data;
  },
  
  // HR Analytics
  getHRAnalytics: async () => {
    const response = await axios.get(`${API}/hr/analytics`, getAuthHeader());
    return response.data;
  },
  
  // Owner Summary Report
  getOwnerSummary: async () => {
    const response = await axios.get(`${API}/reports/owner-summary`, getAuthHeader());
    return response.data;
  },
  
  // Profit Loss Reports
  getProfitLossMonthly: async (year, month) => {
    const params = [];
    if (year) params.push(`year=${year}`);
    if (month) params.push(`month=${month}`);
    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    const response = await axios.get(`${API}/reports/profit-loss-monthly${queryString}`, getAuthHeader());
    return response.data;
  },
  
  getProfitLossComparison: async (year) => {
    const queryString = year ? `?year=${year}` : '';
    const response = await axios.get(`${API}/reports/profit-loss-comparison${queryString}`, getAuthHeader());
    return response.data;
  },

  getProfitLossCombined: async (year, month) => {
    const params = [];
    if (year) params.push(`year=${year}`);
    if (month) params.push(`month=${month}`);
    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    const response = await axios.get(`${API}/reports/profit-loss-combined${queryString}`, getAuthHeader());
    return response.data;
  },
  
  // User Management
  getUsers: async () => {
    const response = await axios.get(`${API}/users`, getAuthHeader());
    return response.data;
  },
  // Autocomplete suggestions (cegah typo nama supplier/buyer)
  getSupplierSuggestions: async () => {
    const response = await axios.get(`${API}/transactions/suppliers/suggestions`, getAuthHeader());
    return response.data;
  },
  getBuyerSuggestions: async () => {
    const response = await axios.get(`${API}/transactions/buyers/suggestions`, getAuthHeader());
    return response.data;
  },
  // Employee Loans (Kasbon)
  createLoan: async (data) => {
    const response = await axios.post(`${API}/hr/loans`, data, getAuthHeader());
    return response.data;
  },
  listLoans: async (status = null, employeeId = null) => {
    const params = [];
    if (status) params.push(`status=${status}`);
    if (employeeId) params.push(`employee_id=${employeeId}`);
    const qs = params.length ? `?${params.join('&')}` : '';
    const response = await axios.get(`${API}/hr/loans${qs}`, getAuthHeader());
    return response.data;
  },
  getLoanSummary: async () => {
    const response = await axios.get(`${API}/hr/loans/summary`, getAuthHeader());
    return response.data;
  },
  getEmployeeOutstanding: async (employeeId) => {
    const response = await axios.get(`${API}/hr/loans/outstanding/${employeeId}`, getAuthHeader());
    return response.data;
  },
  recordLoanPayment: async (loanId, data) => {
    const response = await axios.post(`${API}/hr/loans/${loanId}/payments`, data, getAuthHeader());
    return response.data;
  },
  deleteLoan: async (loanId) => {
    const response = await axios.delete(`${API}/hr/loans/${loanId}`, getAuthHeader());
    return response.data;
  },
  createUser: async (data) => {
    const response = await axios.post(`${API}/users`, data, getAuthHeader());
    return response.data;
  },
  updateUser: async (id, data) => {
    const response = await axios.put(`${API}/users/${id}`, data, getAuthHeader());
    return response.data;
  },
  deleteUser: async (id) => {
    const response = await axios.delete(`${API}/users/${id}`, getAuthHeader());
    return response.data;
  },
  
  // Permissions
  getMyPermissions: async () => {
    const response = await axios.get(`${API}/auth/permissions`, getAuthHeader());
    return response.data;
  },
  
  // Field Transactions (Kasir Lapangan)
  getFieldTransactions: async () => {
    const response = await axios.get(`${API}/field-transactions`, getAuthHeader());
    return response.data;
  },
  createFieldTransaction: async (data) => {
    const response = await axios.post(`${API}/field-transactions`, data, getAuthHeader());
    return response.data;
  },
  updateFieldTransaction: async (id, data) => {
    const response = await axios.put(`${API}/field-transactions/${id}`, data, getAuthHeader());
    return response.data;
  },
  approveFieldTransaction: async (id) => {
    const response = await axios.patch(`${API}/field-transactions/${id}/approve`, {}, getAuthHeader());
    return response.data;
  },
  rejectFieldTransaction: async (id) => {
    const response = await axios.patch(`${API}/field-transactions/${id}/reject`, {}, getAuthHeader());
    return response.data;
  },
  
  // Stock Adjustments
  createAdjustment: async (data) => {
    const response = await axios.post(`${API}/adjustments`, data, getAuthHeader());
    return response.data;
  },
  
  getAdjustments: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.commodity_type) params.append('commodity_type', filters.commodity_type);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await axios.get(`${API}/adjustments${queryString}`, getAuthHeader());
    return response.data;
  },
  
  getPendingAdjustments: async () => {
    const response = await axios.get(`${API}/adjustments/pending`, getAuthHeader());
    return response.data;
  },
  
  getAdjustmentStatistics: async () => {
    const response = await axios.get(`${API}/adjustments/statistics`, getAuthHeader());
    return response.data;
  },
  
  approveAdjustment: async (id, notes = '') => {
    const response = await axios.put(`${API}/adjustments/${id}/approve`, { review_notes: notes }, getAuthHeader());
    return response.data;
  },
  
  rejectAdjustment: async (id, notes = 'Rejected') => {
    const response = await axios.put(`${API}/adjustments/${id}/reject`, { review_notes: notes }, getAuthHeader());
    return response.data;
  },
  
  getAveragePrices: async () => {
    const response = await axios.get(`${API}/adjustments/average-prices`, getAuthHeader());
    return response.data;
  },
  
  // Test Email Config
  testEmailConfig: async (data) => {
    const response = await axios.post(`${API}/profile/test-email`, data, getAuthHeader());
    return response.data;
  },
  
  // Pending Edit Requests
  getPendingEdits: async () => {
    const response = await axios.get(`${API}/pending-edits`, getAuthHeader());
    return response.data;
  },
  getMyPendingEdits: async () => {
    const response = await axios.get(`${API}/pending-edits/my`, getAuthHeader());
    return response.data;
  },
  approvePendingEdit: async (id) => {
    const response = await axios.patch(`${API}/pending-edits/${id}/approve`, {}, getAuthHeader());
    return response.data;
  },
  rejectPendingEdit: async (id, notes = '') => {
    const response = await axios.patch(`${API}/pending-edits/${id}/reject?notes=${encodeURIComponent(notes)}`, {}, getAuthHeader());
    return response.data;
  },
  
  // Password Management
  changePassword: async (currentPassword, newPassword) => {
    const response = await axios.post(`${API}/auth/change-password`, {
      current_password: currentPassword,
      new_password: newPassword
    }, getAuthHeader());
    return response.data;
  },
  resetUserPassword: async (userId, newPassword) => {
    const response = await axios.post(`${API}/auth/reset-password`, {
      user_id: userId,
      new_password: newPassword
    }, getAuthHeader());
    return response.data;
  },
  
  // Audit Logs
  getAuditLogs: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await axios.get(`${API}/audit-logs?${queryString}`, getAuthHeader());
    return response.data;
  },
  getAuditSummary: async () => {
    const response = await axios.get(`${API}/audit-logs/summary`, getAuthHeader());
    return response.data;
  },
  getAuditLogDetail: async (logId) => {
    const response = await axios.get(`${API}/audit-logs/${logId}`, getAuthHeader());
    return response.data;
  },
  getAuditLogPreview: async (logId) => {
    const response = await axios.get(`${API}/audit-logs/${logId}/preview`, getAuthHeader());
    return response.data;
  },
  
  // System Info
  getBackupInfo: async () => {
    const response = await axios.get(`${API}/system/backup-info`, getAuthHeader());
    return response.data;
  },

  getReconciliationStatus: async () => {
    const response = await axios.get(`${API}/system/reconciliation-status`, getAuthHeader());
    return response.data;
  },

  repairCashflowOrphans: async (payload) => {
    const response = await axios.post(`${API}/system/admin/repair-cashflow-orphans`, payload, getAuthHeader());
    return response.data;
  },

  backfillCashflowEntityId: async (payload) => {
    const response = await axios.post(`${API}/system/admin/backfill-cashflow-entity-id`, payload, getAuthHeader());
    return response.data;
  },
  
  // Notifications
  getNotifications: async () => {
    const response = await axios.get(`${API}/notifications`, getAuthHeader());
    return response.data;
  },
  
  // Global Search
  globalSearch: async (query, type = 'all') => {
    const response = await axios.get(`${API}/search?q=${encodeURIComponent(query)}&type=${type}`, getAuthHeader());
    return response.data;
  },
  getSearchDetail: async (itemType, itemId) => {
    const response = await axios.get(`${API}/search/detail/${itemType}/${itemId}`, getAuthHeader());
    return response.data;
  },

  // Analytics - Profit per Supplier
  getProfitPerSupplier: async (months = 3) => {
    const response = await axios.get(`${API}/reports/profit-per-supplier?months=${months}`, getAuthHeader());
    return response.data;
  },

  // Analytics - Cash Burn Rate
  getCashBurnRate: async () => {
    const response = await axios.get(`${API}/reports/cash-burn-rate`, getAuthHeader());
    return response.data;
  },

  // Analytics - Trend Analysis
  getTrendAnalysis: async (months = 6) => {
    const response = await axios.get(`${API}/reports/trend-analysis?months=${months}`, getAuthHeader());
    return response.data;
  },

  // Analytics - Employee Performance
  getEmployeePerformance: async () => {
    const response = await axios.get(`${API}/reports/employee-performance`, getAuthHeader());
    return response.data;
  },

  // Dashboard Preferences
  getDashboardPreferences: async () => {
    const response = await axios.get(`${API}/dashboard/preferences`, getAuthHeader());
    return response.data;
  },
  updateDashboardPreferences: async (widgets) => {
    const response = await axios.put(`${API}/dashboard/preferences`, { widgets }, getAuthHeader());
    return response.data;
  },

  // Export Excel
  exportExcel: async (type = 'all', dateFrom = '', dateTo = '') => {
    let url = `${API}/bulk-import/export?type=${type}`;
    if (dateFrom) url += `&date_from=${dateFrom}`;
    if (dateTo) url += `&date_to=${dateTo}`;
    const response = await axios.get(url, { ...getAuthHeader(), responseType: 'blob' });
    const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = blobUrl;
    let fname = `backup_kansha_${type}`;
    if (dateFrom && dateTo) fname += `_${dateFrom}_sd_${dateTo}`;
    else if (dateFrom) fname += `_dari_${dateFrom}`;
    fname += `_${new Date().toISOString().slice(0,10)}.xlsx`;
    link.setAttribute('download', fname);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  },

  // Restore from backup
  restoreFromBackup: async (file, mode = 'skip-existing') => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(
      `${API}/bulk-import/restore?mode=${mode}`,
      formData,
      { ...getAuthHeader(), headers: { ...getAuthHeader().headers, 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  // Bon Template Settings
  getBonSettings: async () => {
    const response = await axios.get(`${API}/invoice/bon-settings`, getAuthHeader());
    return response.data;
  },
  updateBonSettings: async (data) => {
    const response = await axios.put(`${API}/invoice/bon-settings`, data, getAuthHeader());
    return response.data;
  },

  // Operational Category Migration
  previewCategoryMigration: async () => {
    const response = await axios.post(`${API}/operational/migrate-categories`, {}, getAuthHeader());
    return response.data;
  },
  applyCategoryMigration: async () => {
    const response = await axios.post(`${API}/operational/apply-migration`, {}, getAuthHeader());
    return response.data;
  },

  // SPSI Titipan
  getSpsiBalance: async () => {
    const response = await axios.get(`${API}/spsi/balance`, getAuthHeader());
    return response.data;
  },
  getSpsiDeposits: async () => {
    const response = await axios.get(`${API}/spsi/deposits`, getAuthHeader());
    return response.data;
  },
  createSpsiDeposit: async (data) => {
    const response = await axios.post(`${API}/spsi/deposits`, data, getAuthHeader());
    return response.data;
  },
  deleteSpsiDeposit: async (id) => {
    const response = await axios.delete(`${API}/spsi/deposits/${id}`, getAuthHeader());
    return response.data;
  },
  migrateSpsiFromOperational: async () => {
    const response = await axios.post(`${API}/spsi/migrate-from-operational`, {}, getAuthHeader());
    return response.data;
  },
  
  // Invoice / Print
  // Invoice Data for Modal Display (JSON)
  getPurchaseInvoice: async (id) => {
    const response = await axios.get(`${API}/invoice/purchase/${id}/data`, getAuthHeader());
    return response.data;
  },
  getSaleInvoice: async (id) => {
    const response = await axios.get(`${API}/invoice/sale/${id}/data`, getAuthHeader());
    return response.data;
  },
  getReceivableInvoice: async (id) => {
    const response = await axios.get(`${API}/invoice/receivable/${id}/data`, getAuthHeader());
    return response.data;
  },
  
  // User Profile
  getProfile: async () => {
    const response = await axios.get(`${API}/profile`, getAuthHeader());
    return response.data;
  },
  updateProfile: async (data) => {
    const params = new URLSearchParams();
    if (data.full_name) params.append('full_name', data.full_name);
    if (data.email) params.append('email', data.email);
    const response = await axios.put(`${API}/profile?${params.toString()}`, {}, getAuthHeader());
    return response.data;
  },
  
  // AI Assistant
  sendAIMessage: async (message, sessionId = null, isVoice = false) => {
    const response = await axios.post(`${API}/ai-assistant/chat`, {
      message,
      session_id: sessionId,
      is_voice: isVoice
    }, getAuthHeader());
    return response.data;
  },
  getAIAssistantSettings: async () => {
    const response = await axios.get(`${API}/ai-assistant/settings`, getAuthHeader());
    return response.data;
  },
  updateAIAssistantSettings: async (data) => {
    const response = await axios.put(`${API}/ai-assistant/settings`, data, getAuthHeader());
    return response.data;
  },
  confirmAIAction: async (sessionId, confirm) => {
    const response = await axios.post(`${API}/ai-assistant/chat`, {
      message: confirm ? 'Ya' : 'Tidak',
      session_id: sessionId,
      confirm_action: confirm
    }, getAuthHeader());
    return response.data;
  },
  getAIChatSessions: async () => {
    const response = await axios.get(`${API}/ai-assistant/sessions`, getAuthHeader());
    return response.data;
  },
  getAIChatMessages: async (sessionId) => {
    const response = await axios.get(`${API}/ai-assistant/sessions/${sessionId}`, getAuthHeader());
    return response.data;
  },
  deleteAIChatSession: async (sessionId) => {
    const response = await axios.delete(`${API}/ai-assistant/sessions/${sessionId}`, getAuthHeader());
    return response.data;
  },
  saveOcrTransaction: async (ocrData, entityId = null) => {
    const response = await axios.post(`${API}/ai-assistant/save-ocr`, {
      ocr_data: ocrData,
      entity_id: entityId,
    }, getAuthHeader());
    return response.data;
  },
  
  // Owner Expenses
  getOwnerExpenses: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.category) queryParams.append('category', params.category);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await axios.get(`${API}/owner-expenses${query}`, getAuthHeader());
    return response.data;
  },
  getOwnerExpenseSummary: async () => {
    const response = await axios.get(`${API}/owner-expenses/summary`, getAuthHeader());
    return response.data;
  },
  createOwnerExpense: async (data) => {
    const response = await axios.post(`${API}/owner-expenses`, data, getAuthHeader());
    return response.data;
  },
  updateOwnerExpense: async (id, data) => {
    const response = await axios.put(`${API}/owner-expenses/${id}`, data, getAuthHeader());
    return response.data;
  },
  deleteOwnerExpense: async (id, reason = '') => {
    const response = await axios.delete(`${API}/owner-expenses/${id}?reason=${encodeURIComponent(reason)}`, getAuthHeader());
    return response.data;
  },
  getOwnerExpensePendingRequests: async () => {
    const response = await axios.get(`${API}/owner-expenses/pending-requests`, getAuthHeader());
    return response.data;
  },
  approveOwnerExpenseRequest: async (requestId) => {
    const response = await axios.post(`${API}/owner-expenses/pending-requests/${requestId}/approve`, {}, getAuthHeader());
    return response.data;
  },
  rejectOwnerExpenseRequest: async (requestId, notes = '') => {
    const response = await axios.post(`${API}/owner-expenses/pending-requests/${requestId}/reject?notes=${encodeURIComponent(notes)}`, {}, getAuthHeader());
    return response.data;
  },
  migrateToOwnerExpense: async (keyword, category) => {
    const response = await axios.post(`${API}/owner-expenses/migrate-from-cashflow?description_keyword=${encodeURIComponent(keyword)}&category=${encodeURIComponent(category)}`, {}, getAuthHeader());
    return response.data;
  },
  deleteFromCashflow: async (keyword) => {
    const response = await axios.delete(`${API}/owner-expenses/delete-from-cashflow?description_keyword=${encodeURIComponent(keyword)}`, getAuthHeader());
    return response.data;
  },

  // ==================== AI Audit Center ====================
  getAuditReport: async () => {
    const response = await axios.get(`${API}/audit-center/report`, getAuthHeader());
    return response.data;
  },
  analyzeAuditReport: async (reportText, provider = 'openai', model = null) => {
    const response = await axios.post(`${API}/audit-center/analyze`,
      { report_text: reportText, provider, model },
      { ...getAuthHeader(), timeout: 90000 }
    );
    return response.data;
  },

  // ==================== Period Lock (Kunci Periode) ====================
  getPeriodLocks: async () => {
    const response = await axios.get(`${API}/period-lock`, getAuthHeader());
    return response.data;
  },
  createPeriodLock: async (year, month, reason = '') => {
    const response = await axios.post(`${API}/period-lock`, { year, month, reason }, getAuthHeader());
    return response.data;
  },
  unlockPeriod: async (year, month) => {
    const response = await axios.delete(`${API}/period-lock/${year}/${month}`, getAuthHeader());
    return response.data;
  },
  getPeriodLockAuditHistory: async () => {
    const response = await axios.get(`${API}/period-lock/audit-history`, getAuthHeader());
    return response.data;
  },
};